import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Editor } from '@tiptap/core';

interface NavigatorEntry {
  type: 'act' | 'scene';
  text: string;
  subtitle: string;
  pos: number; // position in document for scrolling
  nodeIndex: number; // index in doc.content
}

interface ActGroup {
  act: NavigatorEntry;
  scenes: NavigatorEntry[];
}

function extractText(node: any): string {
  let result = '';
  node.descendants((child: any) => {
    if (child.isText) result += child.text;
  });
  return result.trim();
}

function buildNavigatorTree(editor: Editor): ActGroup[] {
  const doc = editor.state.doc;
  const entries: NavigatorEntry[] = [];

  let offset = 0;
  doc.forEach((node, nodeOffset, index) => {
    if (node.type.name === 'actHeading') {
      entries.push({
        type: 'act',
        text: extractText(node) || 'Untitled Act',
        subtitle: '',
        pos: nodeOffset + 1, // +1 to get inside the node
        nodeIndex: index,
      });
    } else if (node.type.name === 'sceneHeading') {
      entries.push({
        type: 'scene',
        text: extractText(node) || 'Untitled Scene',
        subtitle: '',
        pos: nodeOffset + 1,
        nodeIndex: index,
      });
    }
  });

  // Find subtitles: first stage direction or dialogue after each heading
  doc.forEach((node, nodeOffset, index) => {
    if (node.type.name === 'stageDirection' || node.type.name === 'dialogue') {
      const text = extractText(node);
      if (!text) return;
      const truncated = text.length > 50 ? text.slice(0, 47) + '...' : text;

      // Find which entry this belongs to (the nearest heading before it)
      for (let i = entries.length - 1; i >= 0; i--) {
        if (entries[i].nodeIndex < index && !entries[i].subtitle) {
          entries[i].subtitle = truncated;
          break;
        }
      }
    }
  });

  // Group into act → scenes structure
  const groups: ActGroup[] = [];
  let currentAct: ActGroup | null = null;

  for (const entry of entries) {
    if (entry.type === 'act') {
      currentAct = { act: entry, scenes: [] };
      groups.push(currentAct);
    } else if (entry.type === 'scene') {
      if (!currentAct) {
        // Scenes before first act — create implicit act group
        currentAct = {
          act: { type: 'act', text: '(No Act)', subtitle: '', pos: 0, nodeIndex: -1 },
          scenes: [],
        };
        groups.push(currentAct);
      }
      currentAct.scenes.push(entry);
    }
  }

  // If there are no acts at all but there are scenes, they're already grouped above
  // If there's nothing, return empty
  return groups;
}

interface NavigatorPanelProps {
  editor: Editor;
  onClose: () => void;
}

export function NavigatorPanel({ editor, onClose }: NavigatorPanelProps) {
  const [tree, setTree] = useState<ActGroup[]>([]);
  const [collapsedActs, setCollapsedActs] = useState<Set<number>>(new Set());
  const [currentPos, setCurrentPos] = useState(0);
  const [editingEntry, setEditingEntry] = useState<NavigatorEntry | null>(null);
  const [editText, setEditText] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  // Rebuild tree on document changes
  useEffect(() => {
    const update = () => setTree(buildNavigatorTree(editor));
    update();
    editor.on('update', update);
    return () => { editor.off('update', update); };
  }, [editor]);

  // Track current cursor position
  useEffect(() => {
    const update = () => {
      const { from } = editor.state.selection;
      setCurrentPos(from);
    };
    update();
    editor.on('selectionUpdate', update);
    return () => { editor.off('selectionUpdate', update); };
  }, [editor]);

  const handleNavigate = useCallback((pos: number) => {
    editor.chain().focus().setTextSelection(pos).run();
    // Scroll the heading into view
    const dom = editor.view.domAtPos(pos);
    if (dom?.node) {
      const el = dom.node instanceof HTMLElement ? dom.node : dom.node.parentElement;
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [editor]);

  const toggleCollapse = useCallback((actIndex: number) => {
    setCollapsedActs(prev => {
      const next = new Set(prev);
      if (next.has(actIndex)) next.delete(actIndex);
      else next.add(actIndex);
      return next;
    });
  }, []);

  const handleStartEdit = useCallback((entry: NavigatorEntry) => {
    setEditingEntry(entry);
    setEditText(entry.text);
    setTimeout(() => editInputRef.current?.focus(), 0);
  }, []);

  const handleFinishEdit = useCallback(() => {
    if (!editingEntry || !editText.trim()) {
      setEditingEntry(null);
      return;
    }

    // Find and replace the text in the actual document node
    const doc = editor.state.doc;
    let targetPos = -1;
    let targetEnd = -1;
    doc.forEach((node, offset) => {
      if (offset + 1 === editingEntry.pos) {
        targetPos = offset + 1; // start of content
        targetEnd = offset + node.nodeSize - 1; // end of content
      }
    });

    if (targetPos >= 0) {
      editor.chain()
        .focus()
        .insertContentAt({ from: targetPos, to: targetEnd }, editText.trim())
        .run();
    }

    setEditingEntry(null);
  }, [editor, editingEntry, editText]);

  const handleAddAct = useCallback(() => {
    editor.chain()
      .focus()
      .insertContentAt(editor.state.selection.from, {
        type: 'actHeading',
        content: [{ type: 'text', text: 'New Act' }],
      })
      .run();
  }, [editor]);

  const handleAddScene = useCallback(() => {
    editor.chain()
      .focus()
      .insertContentAt(editor.state.selection.from, {
        type: 'sceneHeading',
        content: [{ type: 'text', text: 'New Scene' }],
      })
      .run();
  }, [editor]);

  const handleDeleteSection = useCallback((entry: NavigatorEntry) => {
    if (entry.nodeIndex < 0) return;

    // Find the range: from this heading to the next heading of same or higher level
    const doc = editor.state.doc;
    let startOffset = -1;
    let endOffset = -1;

    doc.forEach((node, offset, index) => {
      if (index === entry.nodeIndex) {
        startOffset = offset;
      } else if (startOffset >= 0 && endOffset < 0) {
        if (entry.type === 'act' && node.type.name === 'actHeading') {
          endOffset = offset;
        } else if (entry.type === 'scene' && (node.type.name === 'sceneHeading' || node.type.name === 'actHeading')) {
          endOffset = offset;
        }
      }
    });

    if (endOffset < 0) endOffset = doc.content.size;
    if (startOffset >= 0) {
      editor.chain().focus().deleteRange({ from: startOffset, to: endOffset }).run();
    }
  }, [editor]);

  // Determine which entry is "current" based on cursor position
  const currentEntry = useMemo(() => {
    let closest: NavigatorEntry | null = null;
    for (const group of tree) {
      if (group.act.pos > 0 && group.act.pos <= currentPos) closest = group.act;
      for (const scene of group.scenes) {
        if (scene.pos <= currentPos) closest = scene;
      }
    }
    return closest;
  }, [tree, currentPos]);

  const isEmpty = tree.length === 0;

  return (
    <div className="w-64 border-l border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 flex flex-col h-full animate-slide-in-right">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-ink-200 dark:border-ink-700">
        <span className="text-xs font-medium text-ink-600 dark:text-ink-400 uppercase tracking-wider">Navigator</span>
        <div className="flex items-center gap-1">
          <button
            onClick={handleAddAct}
            className="text-[10px] text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 px-1.5 py-0.5 rounded hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
            title="Add act heading"
          >
            +Act
          </button>
          <button
            onClick={handleAddScene}
            className="text-[10px] text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 px-1.5 py-0.5 rounded hover:bg-ink-100 dark:hover:bg-ink-800 transition-colors"
            title="Add scene heading"
          >
            +Scene
          </button>
          <button
            onClick={onClose}
            className="text-ink-400 dark:text-ink-500 hover:text-ink-700 dark:hover:text-ink-300 ml-1 transition-colors"
            title="Close navigator"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tree */}
      <div className="flex-1 overflow-y-auto py-2">
        {isEmpty && (
          <p className="text-xs text-ink-400 dark:text-ink-500 px-3 py-4 text-center italic">
            No acts or scenes yet. Add act/scene headings to see structure here.
          </p>
        )}

        {tree.map((group, gi) => (
          <div key={gi} className="mb-1">
            {/* Act heading */}
            {group.act.nodeIndex >= 0 && (
              <button
                onClick={() => toggleCollapse(gi)}
                onDoubleClick={() => handleStartEdit(group.act)}
                className={`w-full text-left px-3 py-1 flex items-center gap-1.5 text-xs transition-colors group ${
                  currentEntry === group.act
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'text-ink-700 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-800'
                }`}
              >
                <svg
                  width="10" height="10" viewBox="0 0 10 10" fill="currentColor"
                  className={`shrink-0 transition-transform ${collapsedActs.has(gi) ? '' : 'rotate-90'}`}
                >
                  <path d="M3 1l5 4-5 4V1z" />
                </svg>
                {editingEntry === group.act ? (
                  <input
                    ref={editInputRef}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onBlur={handleFinishEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFinishEdit();
                      if (e.key === 'Escape') setEditingEntry(null);
                    }}
                    className="flex-1 bg-white dark:bg-ink-800 border border-blue-300 dark:border-blue-600 rounded px-1 text-xs outline-none"
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span
                    className="font-medium uppercase truncate cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigate(group.act.pos);
                    }}
                  >
                    {group.act.text}
                  </span>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm('Delete this act and all its contents?')) {
                      handleDeleteSection(group.act);
                    }
                  }}
                  className="ml-auto opacity-0 group-hover:opacity-100 text-ink-400 dark:text-ink-500 hover:text-red-500 transition-all"
                  title="Delete act"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              </button>
            )}

            {/* Scenes */}
            {!collapsedActs.has(gi) && group.scenes.map((scene, si) => (
              <div
                key={si}
                className={`group flex items-start gap-1.5 pl-7 pr-3 py-1 cursor-pointer transition-colors ${
                  currentEntry === scene
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'text-ink-600 dark:text-ink-400 hover:bg-ink-50 dark:hover:bg-ink-800'
                }`}
                onClick={() => handleNavigate(scene.pos)}
                onDoubleClick={() => handleStartEdit(scene)}
              >
                <div className="flex-1 min-w-0">
                  {editingEntry === scene ? (
                    <input
                      ref={editInputRef}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onBlur={handleFinishEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleFinishEdit();
                        if (e.key === 'Escape') setEditingEntry(null);
                      }}
                      className="w-full bg-white dark:bg-ink-800 border border-blue-300 dark:border-blue-600 rounded px-1 text-xs outline-none"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div className="text-xs truncate">{scene.text}</div>
                      {scene.subtitle && (
                        <div className="text-[10px] text-ink-400 dark:text-ink-500 truncate">{scene.subtitle}</div>
                      )}
                    </>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm('Delete this scene and its contents?')) {
                      handleDeleteSection(scene);
                    }
                  }}
                  className="mt-0.5 opacity-0 group-hover:opacity-100 text-ink-400 dark:text-ink-500 hover:text-red-500 transition-all shrink-0"
                  title="Delete scene"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
